
import { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { Toaster } from '@/components/ui/toaster';
import { healthMonitor } from '@/lib/healthCheck'; // ✅ Import healthMonitor
// Wrappers pour séparer Landing et App
import { LandingWrapper } from './landing/LandingWrapper';
import { AppWrapper } from './app/AppWrapper';
import { InvitationHandler } from './components/InvitationHandler';
// Pages critiques chargées immédiatement
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import SubmitReferents from './pages/SubmitReferents';
import NotFound from './pages/NotFound';
import AcceptInvitation from "./pages/AcceptInvitation";
import InviteSignup from "./pages/InviteSignup";
import Unsubscribe from './pages/Unsubscribe';
import AttestationUpload from './pages/AttestationUpload';

// Pages chargées directement (plus de lazy loading)
import Dashboard from './pages/Dashboard';
import CreateCampaign from './pages/CreateCampaign';
import Reports from './pages/Reports';
import Statistiques from './pages/Statistiques';
import ReferentForm from './pages/ReferentForm';
import Settings from './pages/Settings';
import OrganizationManagement from './pages/OrganizationManagement';
import Subscription from './pages/Subscription';
import SubscriptionNew from './pages/SubscriptionNew';
import HelpSupport from './pages/HelpSupport';
import MonEquipe from './pages/MonEquipe';
import Conformite from './pages/Conformite';
import { CreditPurchaseSuccess } from './pages/credits/CreditPurchaseSuccess';
import { CreditPurchaseCancel } from './pages/credits/CreditPurchaseCancel';
import { RequireAuth } from './components/auth/RequireAuth';
import { RequireCredits } from './components/auth/RequireCredits';

import { useAuth } from './hooks/useAuth';
import { useIdleTimer } from './hooks/useIdleTimer';
import { useNavigate } from 'react-router-dom';
import { SupabaseConnectionTest } from './components/SupabaseConnectionTest';

function AppContent() {
  const { user, isRecoveryMode } = useAuth();
  const navigate = useNavigate();

  // ✅ Reset healthMonitor au démarrage pour éviter les compteurs obsolètes
  useEffect(() => {
    healthMonitor.reset();
  }, []); // Une seule fois au montage

  // Déconnexion automatique après 15 minutes d'inactivité
  useIdleTimer({
    timeout: 15 * 60 * 1000, // 15 minutes
    enabled: !!user // Activer seulement si utilisateur connecté
  });

  useEffect(() => {
    // ✅ Vérifier si un token d'invitation est présent
    // TODO: Refactorer pour utiliser useSearchParams au lieu de window.location
    const params = new URLSearchParams(window.location.search);
    const hasInviteToken = params.has("invite_token") || params.has("token");
    
    // Redirect to dashboard if already logged in AND email is confirmed
    // Ne pas rediriger si l'utilisateur vient de s'inscrire (email non confirmé)
    // Ne pas rediriger si l'utilisateur est en mode recovery (reset password)
    // ✅ Ne pas rediriger si invite_token présent (laisser le hook InvitationHandler gérer)
    if (user && !isRecoveryMode && window.location.pathname === '/auth') {
      // Vérifier si l'email est confirmé
      const emailConfirmed = user.email_confirmed_at !== null;
      
      if (emailConfirmed && !hasInviteToken) {
        navigate('/mon-equipe');
      }
      // Si email non confirmé, on reste sur /auth pour que l'utilisateur puisse se connecter après confirmation
      // Si invite_token présent, on reste sur /auth pour que InvitationHandler accepte l'invitation
    }
  }, [user, isRecoveryMode, navigate]);

  // Helper function to get initial sidebar state from cookie
  const getInitialSidebarState = () => {
    if (typeof document === 'undefined') return true; // SSR safety - default to expanded
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('sidebar:state='));
    return cookie ? cookie.split('=')[1] === 'true' : true; // Default to expanded for new users
  };

  return (
    <>
      <SupabaseConnectionTest />
      <Toaster />
      <Routes>
        {/* Landing page - Styles isolés, SANS providers */}
        <Route path="/" element={<LandingWrapper />} />
        
        {/* App - Styles isolés, AVEC providers */}
        <Route path="/*" element={
          <AppWrapper>
            <SidebarProvider defaultOpen={getInitialSidebarState()}>
              <LayoutProvider defaultLayout="default" defaultSidebarVisible={true}>
                {/* ✅ Handler global pour les invitations */}
                <InvitationHandler />
                
                <Routes>
                <Route
                  path="/auth"
                  element={<Auth />}
                />
          <Route
            path="/auth/reset-password"
            element={<ResetPassword />}
          />
          
          {/* Routes protégées - Nécessitent une authentification */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/mon-equipe"
            element={
              <RequireAuth>
                <MonEquipe />
              </RequireAuth>
            }
          />
          <Route
            path="/conformite"
            element={
              <RequireAuth>
                <Conformite />
              </RequireAuth>
            }
          />
          <Route
            path="/reports"
            element={
              <RequireAuth>
                <Reports />
              </RequireAuth>
            }
          />
          <Route
            path="/statistiques"
            element={
              <RequireAuth>
                <Statistiques />
              </RequireAuth>
            }
          />
          <Route 
            path="/help" 
            element={
              <RequireAuth>
                <HelpSupport />
              </RequireAuth>
            } 
          />
          <Route 
            path="/help-support" 
            element={
              <RequireAuth>
                <HelpSupport />
              </RequireAuth>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            } 
          />
          <Route 
            path="/settings/:tab" 
            element={
              <RequireAuth>
                <Settings />
              </RequireAuth>
            } 
          />
          <Route 
            path="/organization" 
            element={
              <RequireAuth>
                <OrganizationManagement />
              </RequireAuth>
            } 
          />
          <Route 
            path="/subscription" 
            element={
              <RequireAuth>
                <Subscription />
              </RequireAuth>
            } 
          />
          <Route 
            path="/subscription/:tab" 
            element={
              <RequireAuth>
                <Subscription />
              </RequireAuth>
            } 
          />
          <Route 
            path="/subscription-new" 
            element={
              <RequireAuth>
                <SubscriptionNew />
              </RequireAuth>
            } 
          />
          <Route 
            path="/subscription-new/:tab" 
            element={
              <RequireAuth>
                <SubscriptionNew />
              </RequireAuth>
            } 
          />
          
          {/* Routes de crédits - Protégées */}
          <Route 
            path="/credits/success" 
            element={
              <RequireAuth>
                <CreditPurchaseSuccess />
              </RequireAuth>
            } 
          />
          <Route 
            path="/credits/cancel" 
            element={
              <RequireAuth>
                <CreditPurchaseCancel />
              </RequireAuth>
            } 
          />

          {/* Profile - Protégé */}
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile onNavigateToHome={() => navigate('/')} />
              </RequireAuth>
            }
          />
          {/* Create Campaign - Protégé par RequireAuth ET RequireCredits */}
          <Route
            path="/create-campaign"
            element={
              <RequireAuth>
                <RequireCredits>
                  <CreateCampaign />
                </RequireCredits>
              </RequireAuth>
            }
          />
          <Route
            path="/submit-referents/:token"
            element={<SubmitReferents />}
          />
          <Route
            path="/referent/:token"
            element={<ReferentForm />}
          />
          <Route
            path="/attestation-upload/:token"
            element={<AttestationUpload />}
          />
          <Route path="/invite/accept" element={<AcceptInvitation />} />
          <Route path="/invite/signup" element={<InviteSignup />} />
          <Route path="/unsubscribe/:referentId" element={<Unsubscribe />} />
          <Route path="*" element={<NotFound />} />
                </Routes>
              </LayoutProvider>
            </SidebarProvider>
          </AppWrapper>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppContent />
    </Router>
  );
}

export default App;
